import {
  Alert,
  Button,
  ButtonGroup,
  HStack,
  RadioGroup,
  Separator,
  Spinner,
  Stack,
  Text,
} from "@chakra-ui/react";
import { observer } from "mobx-react";
import { useStores } from "../../../../../shared/stores/RootStore";
import { useEffect, useState } from "react";
import { LngLat } from "mapbox-gl";
import { MapillaryApi } from "../../../../../map/services/MapillaryApi";
import { MapillarySequence } from "../../../../../map/models/Mapillary";
import { toast, toaster } from "../../../../../shared/views/CustomToaster";
import { formatDateString } from "../../../../../shared/utils/format-utils";
import { JobStateApi } from "../../../../../shared/services/JobStateApi";
import { JobStatus } from "../../../../../shared/models/JobState";
import { delay } from "@keita/lanepatrol-sdk";
import { MapMode } from "../../../../../survey/stores/MapStore";

const MapillaryImportControlsBase = ({
  onCancel,
}: {
  onCancel: () => void;
}) => {
  const { mapStore, craNodeStore, craWayStore, mapillaryStore } = useStores();

  const craWay = craWayStore.getSelected()!;

  const [awaitingPoint, setAwaitingPoint] = useState(true);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    mapStore.setMode(MapMode.MapillaryImport);
    return () => {
      mapStore.setMode(MapMode.SurveyPage);
    };
  }, [mapStore]);

  const onSelectPoint = async (lngLat: LngLat) => {
    setLoading(true);

    let radius = 0.0002;

    const toastId = toast("loading", "Starting search...");

    const attempt = async (): Promise<MapillarySequence[]> => {
      console.log("Radius:", radius);

      return await MapillaryApi.querySequences({
        lat: lngLat.lat,
        lng: lngLat.lng,
        radius,
      })
        .then(({ data }) => {
          console.log(data);
          return data;
        })
        .catch((e) => {
          console.error(e);
          return [];
        });
    };

    let results: MapillarySequence[] = [];

    while (radius < 0.01) {
      results = await attempt();

      if (results.length) {
        break;
      }

      toaster.update(toastId, {
        title: `No results found. Retrying with bigger search radius: ${radius}`,
      });

      await delay(1000);
      radius *= 2;
    }

    toaster.remove(toastId);

    if (results.length) {
      mapillaryStore.setSequences(results);
    } else {
      toast("error", "Could not find any photos near that location");
    }

    setLoading(false);
  };

  useEffect(() => {
    if (awaitingPoint) {
      const stopListening = mapStore.listenForClick((e) => {
        onSelectPoint(e.lngLat);
        setAwaitingPoint(false);
      });

      return stopListening;
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [awaitingPoint]);

  const shiftKey = "⇧";

  const onConfirm = async () => {
    const toastId = toast("loading", "Initiating import...");
    setLoading(true);

    return await MapillaryApi.importSequence({
      craWayId: craWay.id,
      sequenceId: mapillaryStore.selectedSequenceId!,
      excludedImageIds: Array.from(mapillaryStore.removedPointIds),
    })
      .then(({ data: jobStateId }) =>
        JobStateApi.poll(jobStateId, 2000, (jobState) => {
          if (
            jobState.status === JobStatus.RUNNING &&
            jobState.progressTotal > 0
          ) {
            toaster.update(toastId, {
              title: `Importing photos (${jobState.progressValue} of ${jobState.progressTotal})`,
            });
          }

          if (jobState.status === JobStatus.FINISHED) {
            toast("success", "Import done");

            mapillaryStore.reset();

            onCancel();

            craWayStore
              .refreshCraWay(craWay.id)
              .then(() => craNodeStore.fetchNodes(craWay.id));
          }
        }),
      )
      .catch(() => {
        toast("error", "An error ocurred. Please retry.");
      })
      .finally(() => {
        toaster.remove(toastId);
        setLoading(false);
      });
  };

  if (loading) {
    return (
      <HStack justifyContent="center" p={4}>
        <Spinner size="lg" color="primary.500" />
      </HStack>
    );
  }

  if (mapillaryStore.sequences.length) {
    const selectedPointCount = mapillaryStore.selectedPointIds.size;

    return (
      <Stack>
        <Text fontSize="sm">Select a set of photos to import</Text>

        <RadioGroup.Root
          colorPalette="primary"
          value={mapillaryStore.selectedSequenceId}
          onValueChange={(e) => mapillaryStore.selectSequence(e.value!!)}
        >
          <Stack separator={<Separator />} gap={0}>
            {mapillaryStore.sequences.map((s) => {
              const isSelected =
                mapillaryStore.selectedSequenceId === s.sequenceId;

              return (
                <RadioGroup.Item
                  key={s.sequenceId}
                  value={s.sequenceId}
                  cursor="pointer"
                  _hover={{ bgColor: "primary.50" }}
                  p={2}
                >
                  <RadioGroup.ItemHiddenInput />
                  <RadioGroup.ItemIndicator />
                  <RadioGroup.ItemText width="100%">
                    <Stack gap={2} width="100%">
                      <Stack gap={0}>
                        <Text fontSize="sm">
                          {formatDateString(s.capturedAt)}
                        </Text>
                        <Text fontSize="xs">Captured by {s.username}</Text>
                        {s.isPanoramic && (
                          <Text fontSize="xs">*panoramic shot*</Text>
                        )}
                      </Stack>

                      {isSelected && selectedPointCount > 0 && (
                        <Stack gap={2} mt={1}>
                          <Text
                            fontSize="xs"
                            fontWeight="semibold"
                            color="fg.muted"
                          >
                            Selected nodes to delete: {selectedPointCount}
                          </Text>

                          <ButtonGroup size="xs" width="100%">
                            <Button
                              flex={1}
                              variant="outline"
                              disabled={!selectedPointCount}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                mapillaryStore.clearSelectedPoints();
                              }}
                            >
                              Clear selection
                            </Button>

                            <Button
                              flex={1}
                              colorPalette="red"
                              variant="outline"
                              disabled={!selectedPointCount}
                              onClick={(event) => {
                                event.preventDefault();
                                event.stopPropagation();
                                mapillaryStore.removeSelectedPoints();
                              }}
                            >
                              Delete
                            </Button>
                          </ButtonGroup>
                        </Stack>
                      )}
                    </Stack>
                  </RadioGroup.ItemText>
                </RadioGroup.Item>
              );
            })}
          </Stack>
        </RadioGroup.Root>

        <ButtonGroup
          alignSelf="end"
          fontSize="sm"
          size="sm"
          colorPalette="primary"
        >
          <Button
            variant="ghost"
            onClick={() => {
              mapillaryStore.setSequences([]);
              setAwaitingPoint(true);
            }}
          >
            Cancel
          </Button>
          <Button
            disabled={!mapillaryStore.selectedSequenceId}
            onClick={onConfirm}
          >
            Import route
          </Button>
        </ButtonGroup>
        {mapillaryStore.selectedSequenceId && (
          <Alert.Root status={"info"}>
            <Alert.Indicator />
            <Alert.Title>
              Hold {shiftKey} and drag on the selected route to select nodes to
              delete.
            </Alert.Title>
          </Alert.Root>
        )}
      </Stack>
    );
  }

  return (
    <Stack>
      <Text fontSize="sm">Select a street by clicking a point in the map</Text>

      <ButtonGroup
        alignSelf="end"
        fontSize="sm"
        size="sm"
        colorPalette="primary"
      >
        <Button variant="ghost" onClick={onCancel}>
          Cancel
        </Button>
      </ButtonGroup>
    </Stack>
  );
};

export const MapillaryImportControls = observer(MapillaryImportControlsBase);
